import { useCallback, useEffect, useRef, useState } from 'react';
import productionRawMaterialsAPI from '../../services/productionRawMaterialsAPI';
import productionProductRequestsAPI from '../../services/productionProductRequestsAPI';
import {
  loadBatches,
  loadFormulas,
  PRODUCTION_CHANGED_EVENT,
  saveBatches,
  saveFormulas,
} from './productionDomain';
import { mapApiRequestItemToRow, mapApiRequestToRow, PLANNING_CHANGED_EVENT } from './planningDomain';
import {
  notifyProductionProductRequestsChanged,
  PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT,
} from './commercialRequestsUtils';
import {
  loadEgresos,
  loadIngresos,
  loadLots,
  loadMaterials,
  loadReceptions,
  saveEgresos,
  saveIngresos,
  saveLots,
  saveMaterials,
  saveReceptions,
} from './rawMaterialsDomain';

const useProductionStore = () => {
  const usingApiRef = useRef(false);
  const hydratePromiseRef = useRef(null);
  const [formulas, setFormulas] = useState(() => loadFormulas());
  const [batches, setBatches] = useState(() => loadBatches());
  const [requests, setRequests] = useState([]);
  const [requestItems, setRequestItems] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState('');
  const [materials, setMaterials] = useState([]);
  const [lots, setLots] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [inventoryCounts, setInventoryCounts] = useState([]);

  const applyLocalMaterials = useCallback(() => {
    setMaterials(loadMaterials());
    setLots(loadLots());
    setReceptions(loadReceptions());
    setIngresos(loadIngresos());
    setEgresos(loadEgresos());
    setInventoryCounts([]);
  }, []);

  const applyRemoteBundle = useCallback((data) => {
    usingApiRef.current = true;
    setMaterials(Array.isArray(data?.materials) ? data.materials : []);
    setLots(Array.isArray(data?.lots) ? data.lots : []);
    setReceptions(Array.isArray(data?.receptions) ? data.receptions : []);
    setIngresos(Array.isArray(data?.ingresos) ? data.ingresos : []);
    setEgresos(Array.isArray(data?.egresos) ? data.egresos : []);
    setInventoryCounts(Array.isArray(data?.inventory_counts) ? data.inventory_counts : []);
  }, []);

  const refreshRemote = useCallback(async () => {
    const data = await productionRawMaterialsAPI.bootstrap();
    applyRemoteBundle(data);
    return data;
  }, [applyRemoteBundle]);

  const refreshLocalModules = useCallback(() => {
    setFormulas(loadFormulas());
    setBatches(loadBatches());
    if (!usingApiRef.current) {
      applyLocalMaterials();
    }
  }, [applyLocalMaterials]);

  /**
   * Solicitudes de producto: la API es la única fuente de verdad. Las genera el cierre
   * de venta en Comercial y se comparten con Producción › Planificación.
   */
  const refreshRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const [headers, items] = await Promise.all([
        productionProductRequestsAPI.getAll(),
        productionProductRequestsAPI.getAllItems(),
      ]);
      setRequests(Array.isArray(headers) ? headers.map(mapApiRequestToRow) : []);
      setRequestItems(Array.isArray(items) ? items.map(mapApiRequestItemToRow) : []);
      setRequestsError('');
    } catch (error) {
      console.error(error);
      setRequests([]);
      setRequestItems([]);
      setRequestsError(error?.message || 'No se pudieron cargar las solicitudes de producto.');
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  const createProductRequest = useCallback(
    async (payload) => {
      const created = await productionProductRequestsAPI.create(payload);
      await refreshRequests();
      notifyProductionProductRequestsChanged();
      return created;
    },
    [refreshRequests]
  );

  const updateProductRequestPlanning = useCallback(
    async (requestId, changes) => {
      if (!requestId) return null;
      const updated = await productionProductRequestsAPI.updatePlanning(requestId, changes);
      await refreshRequests();
      notifyProductionProductRequestsChanged();
      return updated;
    },
    [refreshRequests]
  );

  const updateProductRequestItemProgress = useCallback(
    async (itemId, progressStatus) => {
      if (!itemId) return null;
      const updated = await productionProductRequestsAPI.updateItemProgress(itemId, progressStatus);
      await refreshRequests();
      notifyProductionProductRequestsChanged();
      return updated;
    },
    [refreshRequests]
  );

  useEffect(() => {
    let cancelled = false;
    hydratePromiseRef.current = productionRawMaterialsAPI
      .bootstrap()
      .then((data) => {
        if (!cancelled) applyRemoteBundle(data);
      })
      .catch(() => {
        if (!cancelled) {
          usingApiRef.current = false;
          applyLocalMaterials();
        }
      });
    window.addEventListener(PRODUCTION_CHANGED_EVENT, refreshLocalModules);
    window.addEventListener(PLANNING_CHANGED_EVENT, refreshLocalModules);
    window.addEventListener('storage', refreshLocalModules);
    return () => {
      cancelled = true;
      window.removeEventListener(PRODUCTION_CHANGED_EVENT, refreshLocalModules);
      window.removeEventListener(PLANNING_CHANGED_EVENT, refreshLocalModules);
      window.removeEventListener('storage', refreshLocalModules);
    };
  }, [applyLocalMaterials, applyRemoteBundle, refreshLocalModules]);

  useEffect(() => {
    refreshRequests();
    window.addEventListener(PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT, refreshRequests);
    return () => window.removeEventListener(PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT, refreshRequests);
  }, [refreshRequests]);

  const persistFormulas = useCallback((updater) => {
    setFormulas((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      saveFormulas(next);
      return next;
    });
  }, []);

  const persistBatches = useCallback((updater) => {
    setBatches((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      saveBatches(next);
      return next;
    });
  }, []);

  const persistMaterials = useCallback((updater) => {
    setMaterials((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (!usingApiRef.current) saveMaterials(next);
      return next;
    });
  }, []);

  const persistLots = useCallback((updater) => {
    setLots((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (!usingApiRef.current) saveLots(next);
      return next;
    });
  }, []);

  const persistReceptions = useCallback((updater) => {
    setReceptions((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (!usingApiRef.current) saveReceptions(next);
      return next;
    });
  }, []);

  const persistIngresos = useCallback((updater) => {
    setIngresos((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (!usingApiRef.current) saveIngresos(next);
      return next;
    });
  }, []);

  const persistEgresos = useCallback((updater) => {
    setEgresos((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (!usingApiRef.current) saveEgresos(next);
      return next;
    });
  }, []);

  const saveMaterialRecord = useCallback(
    async (payload, id = null) => {
      if (hydratePromiseRef.current) {
        await hydratePromiseRef.current.catch(() => null);
      }
      if (usingApiRef.current) {
        if (id) {
          await productionRawMaterialsAPI.update(id, payload);
        } else {
          await productionRawMaterialsAPI.create(payload);
        }
        await refreshRemote();
        return;
      }
      persistMaterials((current) => {
        const now = new Date().toISOString().slice(0, 10);
        if (id) {
          return current.map((row) =>
            Number(row.id) === Number(id) ? { ...row, ...payload, id, updated_at: now } : row
          );
        }
        const nextId = current.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
        return [...current, { ...payload, id: nextId, updated_at: now }];
      });
    },
    [persistMaterials, refreshRemote]
  );

  const saveReceptionRecord = useCallback(
    async (payload) => {
      if (hydratePromiseRef.current) {
        await hydratePromiseRef.current.catch(() => null);
      }
      if (usingApiRef.current) {
        await productionRawMaterialsAPI.createReception(payload);
        await refreshRemote();
        return;
      }
      const now = payload.date || new Date().toISOString().slice(0, 10);
      const lotCode = payload.lot_code || `L-${payload.material_code}-${Date.now()}`;
      persistReceptions((current) => {
        if (payload.reception_id) {
          return current.map((row) =>
            Number(row.id) === Number(payload.reception_id)
              ? {
                  ...row,
                  date: now,
                  quantity: Number(payload.quantity || 0),
                  unit: payload.unit || row.unit || 'kg',
                  warehouse: payload.warehouse || row.warehouse || 'Bodega MP',
                  lot_code: lotCode,
                  supplier: payload.supplier || row.supplier || '',
                  oc_code: payload.oc_code || row.oc_code || '',
                  status: 'Ingresada',
                  batch_params: payload.batch_params || row.batch_params || [],
                }
              : row
          );
        }
        const nextId = current.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
        return [
          {
            id: nextId,
            code: `REC-P-${String(nextId).padStart(3, '0')}`,
            date: now,
            oc_code: payload.oc_code || '',
            material_id: payload.material_id,
            material_code: payload.material_code,
            material_name: payload.material_name || '',
            quantity: Number(payload.quantity || 0),
            unit: payload.unit || 'kg',
            warehouse: payload.warehouse || 'Bodega MP',
            lot_code: lotCode,
            supplier: payload.supplier || '',
            status: 'Ingresada',
            batch_params: payload.batch_params || [],
          },
          ...current,
        ];
      });
      persistLots((current) => {
        const nextId = current.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
        return [
          {
            id: nextId,
            material_id: payload.material_id,
            material_code: payload.material_code,
            material_name: payload.material_name || '',
            lot_code: lotCode,
            warehouse: payload.warehouse || 'Bodega MP',
            quantity: Number(payload.quantity || 0),
            unit: payload.unit || 'kg',
            received_at: now,
            expiry: payload.expiry || null,
            supplier_lot: payload.supplier_lot || '',
            status: 'Disponible',
            batch_params: payload.batch_params || [],
          },
          ...current,
        ];
      });
      persistIngresos((current) => {
        const nextId = current.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
        return [
          {
            id: nextId,
            code: `ING-P-${String(nextId).padStart(3, '0')}`,
            date: now,
            material_id: payload.material_id,
            material_code: payload.material_code,
            material_name: payload.material_name || '',
            quantity: Number(payload.quantity || 0),
            unit: payload.unit || 'kg',
            warehouse: payload.warehouse || 'Bodega MP',
            origin: 'Recepción en planta',
            reference: lotCode,
            status: 'Confirmado',
          },
          ...current,
        ];
      });
    },
    [persistIngresos, persistLots, persistReceptions, refreshRemote]
  );

  return {
    formulas,
    batches,
    requests,
    requestItems,
    requestsLoading,
    requestsError,
    materials,
    lots,
    receptions,
    ingresos,
    egresos,
    inventoryCounts,
    persistFormulas,
    persistBatches,
    createProductRequest,
    updateProductRequestPlanning,
    updateProductRequestItemProgress,
    persistMaterials,
    persistLots,
    persistReceptions,
    persistIngresos,
    persistEgresos,
    saveMaterialRecord,
    saveReceptionRecord,
    refresh: refreshLocalModules,
    refreshRemote,
    refreshRequests,
  };
};

export default useProductionStore;
